import { getPlatformMark } from "@mairko/brand";
import { useState } from "react";
import {
	connectGodaddy,
	connectHref,
	disconnectProvider,
	LIVE_CONNECT_PROVIDERS,
	MATRIX_TO_PLATFORM,
	smokeCloudflare,
	smokeGa4,
	smokeGbp,
	smokeGithub,
	smokeGodaddy,
	smokeGtm,
	smokeLinkedin,
	smokeLinkedinAds,
	smokeVercel,
	smokeX,
	smokeShopify,
	smokeWordpress,
	smokePinterest,
	smokeGmail,
	smokeWix,
	smokeWebflow,
	smokeSquarespace,
	smokeGoogleDrive,
	smokeGooglePhotos,
	smokeCanva,
	smokeFigma,
	smokeDropbox,
	type PlatformConnection,
} from "./connections-api.js";
import { AgentAvatar } from "./AgentAvatar.js";
import { getIntegrationDetail } from "./integration-detail.js";
import { LinkedIdentityPanel, PortfolioPanel } from "./PortfolioPanel.js";
import { AGENTS, type Integration } from "./nav.js";

type Props = {
	item: Integration;
	kind: "connector" | "channel";
	onBack: () => void;
	platformRow?: PlatformConnection | null;
	onConnectionChange?: () => Promise<void> | void;
	oauthFlash?: string | null;
	oauthFlashTone?: "ok" | "err";
};

/** Matrix id → portfolio panel copy. Platform provider id comes from MATRIX_TO_PLATFORM. */
const PORTFOLIO_PANEL: Record<
	string,
	{
		title: string;
		resourceLabel: string;
		variant?: "flat" | "ads" | "account";
		identity?: boolean;
	}
> = {
	ga4: {
		title: "GA4 properties",
		resourceLabel: "property",
		variant: "account",
	},
	google_ads: {
		title: "Ads portfolio",
		resourceLabel: "account",
		variant: "ads",
	},
	gsc: { title: "Search Console sites", resourceLabel: "site" },
	gtm: {
		title: "GTM containers",
		resourceLabel: "container",
		variant: "account",
	},
	gbp: {
		title: "Business Profile locations",
		resourceLabel: "location",
		variant: "account",
	},
	github: { title: "GitHub repositories", resourceLabel: "repository" },
	vercel: { title: "Vercel projects", resourceLabel: "project" },
	cloudflare: {
		title: "Cloudflare zones",
		resourceLabel: "zone",
		variant: "account",
	},
	godaddy: { title: "GoDaddy domains", resourceLabel: "domain" },
	linkedin: { title: "LinkedIn Company Pages", resourceLabel: "page" },
	linkedin_ads: { title: "LinkedIn Ad Accounts", resourceLabel: "account" },
	wordpress: { title: "WordPress sites", resourceLabel: "site" },
	webflow: { title: "Webflow sites", resourceLabel: "site" },
	x: { title: "X account", resourceLabel: "identity", identity: true },
	pinterest: {
		title: "Pinterest account",
		resourceLabel: "identity",
		identity: true,
	},
	email: { title: "Gmail mailbox", resourceLabel: "identity", identity: true },
	shopify: {
		title: "Shopify store",
		resourceLabel: "identity",
		identity: true,
	},
	wix: { title: "Wix site", resourceLabel: "identity", identity: true },
	squarespace: {
		title: "Squarespace website",
		resourceLabel: "identity",
		identity: true,
	},
};

type SmokeRunner = () => Promise<string>;

function smokeRunnerFor(matrixId: string): SmokeRunner | null {
	switch (matrixId) {
		case "ga4":
			return async () => {
				const result = await smokeGa4();
				if (!result.ok) throw new Error(result.error);
				return `OK — ${result.totalSessions} sessions (${result.startDate} → ${result.endDate}) on ${result.propertyId}`;
			};
		case "gtm":
			return async () => {
				const result = await smokeGtm();
				if (!result.ok) throw new Error(result.error);
				return `OK — ${result.name ?? result.containerId}${
					result.publicId ? ` (${result.publicId})` : ""
				}`;
			};
		case "gbp":
			return async () => {
				const result = await smokeGbp();
				if (!result.ok) throw new Error(result.error);
				return `OK — ${result.title ?? result.locationId}`;
			};
		case "github":
			return async () => {
				const result = await smokeGithub();
				if (!result.ok) throw new Error(result.error);
				return `OK — ${result.fullName} @ ${result.defaultBranch}${
					result.private ? " (private)" : ""
				}`;
			};
		case "vercel":
			return async () => {
				const result = await smokeVercel();
				if (!result.ok) throw new Error(result.error);
				const where = result.teamSlug
					? `${result.teamSlug}/${result.name}`
					: result.name;
				return `OK — ${where}${
					result.productionUrl ? ` · ${result.productionUrl}` : ""
				}`;
			};
		case "cloudflare":
			return async () => {
				const result = await smokeCloudflare();
				if (!result.ok) throw new Error(result.error);
				return `OK — ${result.name} (${result.status})${
					result.accountName ? ` · ${result.accountName}` : ""
				}`;
			};
		case "godaddy":
			return async () => {
				const result = await smokeGodaddy();
				if (!result.ok) throw new Error(result.error);
				return `OK — ${result.domain} (${result.status})${
					result.expires ? ` · expires ${result.expires.slice(0, 10)}` : ""
				}`;
			};
		case "linkedin":
			return async () => {
				const result = await smokeLinkedin();
				if (!result.ok) throw new Error(result.error);
				return `OK — ${result.name}${
					result.vanityName ? ` · /${result.vanityName}` : ""
				}`;
			};
		case "linkedin_ads":
			return async () => {
				const result = await smokeLinkedinAds();
				if (!result.ok) throw new Error(result.error);
				return `OK — ${result.name} (${result.status})`;
			};
		case "x":
			return async () => {
				const result = await smokeX();
				if (!result.ok) throw new Error(result.error);
				return `OK — @${result.username} (${result.name})`;
			};
		case "shopify":
			return async () => {
				const result = await smokeShopify();
				if (!result.ok) throw new Error(result.error);
				return `OK — ${result.name} (${result.shop})${
					result.currency ? ` · ${result.currency}` : ""
				}`;
			};
		case "wix":
			return async () => {
				const result = await smokeWix();
				if (!result.ok) throw new Error(result.error);
				return `OK — ${result.siteDisplayName}${
					result.url ? ` · ${result.url}` : ""
				}`;
			};
		case "webflow":
			return async () => {
				const result = await smokeWebflow();
				if (!result.ok) throw new Error(result.error);
				return `OK — ${result.displayName}${
					result.shortName ? ` · ${result.shortName}` : ""
				}`;
			};
		case "squarespace":
			return async () => {
				const result = await smokeSquarespace();
				if (!result.ok) throw new Error(result.error);
				return `OK — ${result.title}${
					result.url ? ` · ${result.url}` : ""
				}`;
			};
		case "wordpress":
			return async () => {
				const result = await smokeWordpress();
				if (!result.ok) throw new Error(result.error);
				return `OK — ${result.name} (${result.url})${
					result.jetpack ? " · Jetpack" : ""
				}`;
			};
		case "pinterest":
			return async () => {
				const result = await smokePinterest();
				if (!result.ok) throw new Error(result.error);
				return `OK — @${result.username}${
					result.accountType ? ` · ${result.accountType}` : ""
				}`;
			};
		case "email":
			return async () => {
				const result = await smokeGmail();
				if (!result.ok) throw new Error(result.error);
				return `OK — ${result.emailAddress}`;
			};
		case "google_drive":
			return async () => {
				const result = await smokeGoogleDrive();
				if (!result.ok) throw new Error(result.error);
				return `OK — ${result.fileCount} file(s)${
					result.sampleName ? ` · ${result.sampleName}` : ""
				}`;
			};
		case "google_photos":
			return async () => {
				const result = await smokeGooglePhotos();
				if (!result.ok) throw new Error(result.error);
				return `OK — ${result.mediaCount} media item(s)${
					result.sampleId ? ` · ${result.sampleId.slice(0, 12)}…` : ""
				}`;
			};
		case "canva":
			return async () => {
				const result = await smokeCanva();
				if (!result.ok) throw new Error(result.error);
				return `OK — ${result.designCount} design(s)${
					result.sampleTitle ? ` · ${result.sampleTitle}` : ""
				}`;
			};
		case "figma":
			return async () => {
				const result = await smokeFigma();
				if (!result.ok) throw new Error(result.error);
				return `OK — @${result.handle}${
					result.email ? ` · ${result.email}` : ""
				}`;
			};
		case "dropbox":
			return async () => {
				const result = await smokeDropbox();
				if (!result.ok) throw new Error(result.error);
				return `OK — ${result.entryCount} entr${
					result.entryCount === 1 ? "y" : "ies"
				}${result.sampleName ? ` · ${result.sampleName}` : ""}`;
			};
		default:
			return null;
	}
}

function ConnectorSmoke({
	matrixId,
	note,
	setNote,
}: {
	matrixId: string;
	note: string | null;
	setNote: (value: string | null) => void;
}) {
	const runner = smokeRunnerFor(matrixId);
	if (!runner) return null;
	return (
		<div className="bind-smoke">
			<button
				type="button"
				className="btn"
				onClick={() => {
					setNote(null);
					void (async () => {
						try {
							setNote(await runner());
						} catch (cause) {
							setNote(
								`Test failed: ${cause instanceof Error ? cause.message : String(cause)}`,
							);
						}
					})();
				}}
			>
				Test connection
			</button>
			{note && <p className="bind-smoke-note">{note}</p>}
		</div>
	);
}

export function IntegrationDetail({
	item,
	kind,
	onBack,
	platformRow = null,
	onConnectionChange,
	oauthFlash = null,
	oauthFlashTone = "ok",
}: Props) {
	const detail = getIntegrationDetail(item.id);
	const mark = getPlatformMark(item.markId, 220);
	const listLabel = kind === "connector" ? "Connectors" : "Channels";
	const live = LIVE_CONNECT_PROVIDERS.has(item.id);
	const conn = platformRow?.connection;
	const linked =
		live &&
		conn &&
		(conn.health === "healthy" || conn.health === "degraded");
	const ctaDisabled =
		detail?.ctaDisabled === true ||
		item.status === "soon" ||
		!live;
	const ctaLabel = linked
		? "Reconnect"
		: (detail?.ctaLabel ?? (kind === "connector" ? "Connect" : "Bind"));

	const [busy, setBusy] = useState(false);
	const [actionError, setActionError] = useState<string | null>(null);
	const [smokeNote, setSmokeNote] = useState<string | null>(null);
	const [showPatForm, setShowPatForm] = useState(false);
	const [patValue, setPatValue] = useState("");
	const [showShopForm, setShowShopForm] = useState(false);
	const [shopValue, setShopValue] = useState("");

	const usesPat = item.id === "godaddy";
	const usesShopForm = item.id === "shopify";
	const portfolioConfig = PORTFOLIO_PANEL[item.id];
	const platformProvider =
		MATRIX_TO_PLATFORM[item.id] ?? item.id;

	const onConnect = () => {
		if (!live) return;
		if (usesPat) {
			setShowPatForm(true);
			setActionError(null);
			return;
		}
		if (usesShopForm) {
			setShowShopForm(true);
			setActionError(null);
			return;
		}
		window.location.href = connectHref(item.id);
	};

	const onSubmitPat = async () => {
		if (!usesPat || !patValue.trim()) return;
		setBusy(true);
		setActionError(null);
		const result = await connectGodaddy(patValue.trim());
		setBusy(false);
		if (!result.ok) {
			setActionError(
				result.error === "invalid_pat"
					? "GoDaddy rejected that token. Generate a PAT with domains.domain:read and try again."
					: result.error,
			);
			return;
		}
		setPatValue("");
		setShowPatForm(false);
		await onConnectionChange?.();
	};

	const onSubmitShop = () => {
		if (!usesShopForm || !shopValue.trim()) return;
		setActionError(null);
		window.location.href = connectHref(item.id, { shop: shopValue.trim() });
	};

	const onDisconnect = async () => {
		if (!live) return;
		setBusy(true);
		setActionError(null);
		const result = await disconnectProvider(item.id);
		setBusy(false);
		if (!result.ok) {
			setActionError(result.error);
			return;
		}
		await onConnectionChange?.();
	};

	type RailAgent = {
		key: string;
		agent: {
			name: string;
			initials: string;
			avatar: string | null;
			avatar2x: string | null;
		};
		role: string;
	};

	const agentRows: RailAgent[] =
		detail?.agents.flatMap((entry): RailAgent[] => {
			if ("agentId" in entry) {
				const agent = AGENTS.find((row) => row.id === entry.agentId);
				if (!agent) return [];
				return [{ key: agent.id, agent, role: entry.role }];
			}
			return [
				{
					key: entry.name,
					agent: {
						name: entry.name,
						initials: entry.name.slice(0, 2).toUpperCase(),
						avatar: null,
						avatar2x: null,
					},
					role: entry.role,
				},
			];
		}) ?? [];

	const scopeLabels = detail
		? [
				...new Set(
					detail.permissions.map((scope) => {
						const leaf = scope.split(".").pop();
						return leaf && leaf.length > 0 ? leaf : scope;
					}),
				),
			]
		: [];

	const gateLines =
		detail?.gates
			.slice(0, 3)
			.map((gate) => gate.split(" — ")[0]?.trim() || gate) ?? [];

	const statusLabel =
		item.status === "soon"
			? "Soon"
			: linked
				? conn?.health === "degraded"
					? "Needs attention"
					: "Connected"
				: "Not connected";

	const showRail =
		Boolean(detail) &&
		(agentRows.length > 0 ||
			gateLines.length > 0 ||
			scopeLabels.length > 0 ||
			(detail?.docs?.length ?? 0) > 0);

	return (
		<div className="detail">
			<button type="button" className="detail-back" onClick={onBack}>
				← {listLabel}
			</button>

			<div className="detail-layout">
			<div className="detail-main">
			<header
				className={
					linked && conn?.health !== "degraded"
						? "detail-hero detail-hero--connected"
						: conn?.health === "degraded"
							? "detail-hero detail-hero--attention"
							: "detail-hero"
				}
			>
				<span className="detail-hero-mark" aria-hidden>
					{mark ? (
						<span
							className="integration-mark"
							dangerouslySetInnerHTML={{ __html: mark.svg }}
						/>
					) : (
						<span className="integration-fallback">
							{item.name.slice(0, 1)}
						</span>
					)}
				</span>
				<div className="detail-hero-body">
					<div className="detail-title-row">
						<h1 className="detail-name">{item.name}</h1>
						<span
							className={
								linked && conn?.health !== "degraded"
									? "integration-badge integration-badge--ok"
									: conn?.health === "degraded"
										? "integration-badge integration-badge--warn"
										: "integration-badge"
							}
						>
							{statusLabel}
						</span>
					</div>
					<p className="detail-blurb">
						{detail?.tagline ?? item.blurb}
					</p>
					<div className="detail-actions">
						<button
							type="button"
							className="btn btn-accent"
							disabled={ctaDisabled || busy}
							onClick={onConnect}
						>
							{ctaLabel}
						</button>
						{linked && (
							<button
								type="button"
								className="btn"
								disabled={busy}
								onClick={() => void onDisconnect()}
							>
								Disconnect
							</button>
						)}
					</div>
				</div>
			</header>

			{(oauthFlash || actionError) && (
				<div className="detail-alerts">
					{oauthFlash && (
						<p
							className={
								oauthFlashTone === "err"
									? "detail-oauth-flash detail-oauth-flash--err"
									: "detail-oauth-flash"
							}
							role={oauthFlashTone === "err" ? "alert" : "status"}
						>
							{oauthFlash}
						</p>
					)}
					{actionError && (
						<p
							className="detail-oauth-flash detail-oauth-flash--err"
							role="alert"
						>
							{actionError}
						</p>
					)}
				</div>
			)}

			{showPatForm && usesPat && (
				<form
					className="bind-panel"
					onSubmit={(event) => {
						event.preventDefault();
						void onSubmitPat();
					}}
				>
					<p className="bind-panel-title">GoDaddy Personal Access Token</p>
					<p className="connection-sub">
						Create a token with <code>domains.domain:read</code> at{" "}
						<a
							href="https://developer.godaddy.com/personal-access-token"
							target="_blank"
							rel="noreferrer"
						>
							developer.godaddy.com/personal-access-token
						</a>
						.
					</p>
					<input
						className="bind-select"
						type="password"
						autoComplete="off"
						spellCheck={false}
						placeholder="Paste PAT…"
						value={patValue}
						disabled={busy}
						onChange={(event) => setPatValue(event.target.value)}
					/>
					<div className="bind-smoke">
						<button
							type="submit"
							className="btn btn-accent"
							disabled={busy || patValue.trim().length < 8}
						>
							{busy ? "Connecting…" : "Save token"}
						</button>
						<button
							type="button"
							className="btn"
							disabled={busy}
							onClick={() => {
								setShowPatForm(false);
								setPatValue("");
							}}
						>
							Cancel
						</button>
					</div>
				</form>
			)}

			{showShopForm && usesShopForm && (
				<form
					className="bind-panel"
					onSubmit={(event) => {
						event.preventDefault();
						onSubmitShop();
					}}
				>
					<p className="bind-panel-title">Shopify store</p>
					<p className="connection-sub">
						Admin subdomain (e.g. <code>your-store.myshopify.com</code>).
					</p>
					<input
						className="bind-select"
						type="text"
						autoComplete="off"
						spellCheck={false}
						placeholder="your-store.myshopify.com"
						value={shopValue}
						disabled={busy}
						onChange={(event) => setShopValue(event.target.value)}
					/>
					<div className="bind-smoke">
						<button
							type="submit"
							className="btn btn-accent"
							disabled={busy || shopValue.trim().length < 3}
						>
							Continue to Shopify
						</button>
						<button
							type="button"
							className="btn"
							disabled={busy}
							onClick={() => {
								setShowShopForm(false);
								setShopValue("");
							}}
						>
							Cancel
						</button>
					</div>
				</form>
			)}

			<section className="detail-setup" aria-label="Setup">
				{linked ? (
					<p className="detail-setup-status">
						{portfolioConfig
							? "Connected — portfolio below is live."
							: "Connected."}
					</p>
				) : (
					<p className="detail-setup-status detail-setup-status--muted">
						{kind === "connector"
							? live
								? "Not connected yet."
								: "Coming soon."
							: "Not connected. Approvals need this channel to reach your team."}
					</p>
				)}

				{linked && portfolioConfig && (
					<>
						{portfolioConfig.identity ? (
							<LinkedIdentityPanel
								provider={platformProvider}
								title={portfolioConfig.title}
								onChanged={async () => {
									await onConnectionChange?.();
								}}
							/>
						) : (
							<PortfolioPanel
								provider={platformProvider}
								title={portfolioConfig.title}
								resourceLabel={portfolioConfig.resourceLabel}
								variant={portfolioConfig.variant ?? "flat"}
								onChanged={async () => {
									await onConnectionChange?.();
								}}
							/>
						)}
						<ConnectorSmoke
							matrixId={item.id}
							note={smokeNote}
							setNote={setSmokeNote}
						/>
					</>
				)}
			</section>
			</div>

			{showRail && detail && (
				<aside className="detail-rail" aria-label="About this connection">
					{agentRows.length > 0 && (
						<div className="detail-rail-block">
							<h2 className="detail-rail-label">On the desk</h2>
							<ul className="detail-rail-agents">
								{agentRows.map((row) => (
									<li key={row.key} className="detail-rail-agent">
										<AgentAvatar agent={row.agent} size="sm" />
										<div className="detail-rail-agent-text">
											<p className="detail-rail-agent-name">{row.agent.name}</p>
											<p className="detail-rail-agent-role">{row.role}</p>
										</div>
									</li>
								))}
							</ul>
						</div>
					)}
					{gateLines.length > 0 && (
						<div className="detail-rail-block">
							<h2 className="detail-rail-label">Approvals</h2>
							<ul className="detail-rail-lines">
								{gateLines.map((line) => (
									<li key={line}>{line}</li>
								))}
							</ul>
						</div>
					)}
					{scopeLabels.length > 0 && (
						<div className="detail-rail-block">
							<h2 className="detail-rail-label">Access</h2>
							<p className="detail-rail-scopes">{scopeLabels.join(" · ")}</p>
						</div>
					)}
					{detail.docs && detail.docs.length > 0 && (
						<div className="detail-rail-block">
							<h2 className="detail-rail-label">Docs</h2>
							<ul className="detail-rail-docs">
								{detail.docs.map((doc) => (
									<li key={doc.href}>
										<a href={doc.href} target="_blank" rel="noreferrer">
											{doc.label}
										</a>
									</li>
								))}
							</ul>
						</div>
					)}
				</aside>
			)}
			</div>
		</div>
	);
}
